import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { TrackingIdService } from '@shared/data';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { SSE } from 'sse.js';
import { Observable, Subject } from 'rxjs';

interface SseError {
  message?: string;
}

// Da für `sse.js` keine Typen existieren, definieren wir ein Interface
// für die Teile der Bibliothek, die wir tatsächlich verwenden. Dies ersetzt `any`.
interface SseInstance {
  addEventListener(type: string, listener: (event: MessageEvent) => void): void;

  onerror: (error: SseError) => void;
  stream: () => void;
  close: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class SseService {
  private readonly oauthService = inject(OAuthService);
  private readonly trackingIdService = inject(TrackingIdService);

  private readonly eventSource: WritableSignal<SseInstance | null> = signal(null);

  private readonly latestEventSubject = new Subject<MessageEvent>();
  public readonly latestEvent$: Observable<MessageEvent> = this.latestEventSubject.asObservable();

  private readonly errorSubject = new Subject<SseError>();
  public readonly error$: Observable<SseError> = this.errorSubject.asObservable();

  private updateListenersAttached = false;

  /**
   * Stellt eine Verbindung zum SSE-Endpunkt her und beginnt, die internen Events zu feuern.
   * Diese Methode sollte nur einmal pro benötigter Verbindung aufgerufen werden.
   * @param url Die URL des SSE-Endpunkts.
   */
  connect(url: string): void {
    // Verhindert mehrfache Verbindungen
    if (this.eventSource()) {
      console.warn('SSE connection already established. Call disconnect() first to start a new one.');
      return;
    }

    this.updateListenersAttached = false; // Reset state on new connection

    const token = this.oauthService.getAccessToken();
    const trackingId = this.trackingIdService.generateTrackingId();
    console.log('TrackingId for sse session:', trackingId);

    const sse: SseInstance = new SSE(url, {
      headers: { Authorization: `Bearer ${token}`, trackingId: trackingId }
    });

    const handleUpdateEvent = (event: MessageEvent) => {
      this.latestEventSubject.next(event);
    };

    const handleInitialEvent = (event: MessageEvent) => {
      // Process the initial event first
      handleUpdateEvent(event);

      // After the initial event has been processed, attach listeners for subsequent updates.
      if (!this.updateListenersAttached) {
        sse.addEventListener('update', handleUpdateEvent);
        sse.addEventListener('stale_update', handleUpdateEvent);
        sse.addEventListener('create', handleUpdateEvent);
        sse.addEventListener('delete', handleUpdateEvent);
        this.updateListenersAttached = true;
      }
    };

    sse.addEventListener('initial', handleInitialEvent);

    sse.addEventListener('message', (event: MessageEvent) => {
      this.latestEventSubject.next(event);
    });

    sse.onerror = (error: SseError) => {
      console.error('SSE Error:', error);
      this.errorSubject.next(error);
    };

    this.eventSource.set(sse);
    sse.stream();
  }

  /**
   * Schließt die aktive SSE-Verbindung und setzt die Verbindungsinstanz zurück.
   */
  disconnect(): void {
    const sse = this.eventSource();
    if (sse) {
      sse.close();
      this.eventSource.set(null);
      this.updateListenersAttached = false;
    }
  }
}
